import { demoAvailability } from './siteContent'

export type DemoStatus = 'interactive' | 'scoped' | 'planned'

export type DemoDomain = {
  /** Stable identifier, also used as the Demo Center anchor (#government). */
  id: 'government' | 'research' | 'education' | 'talent' | 'community' | 'leadership' | 'clinical'
  title: string
  solutionLabel: string
  description: string
  workspaces: string[]
  status: DemoStatus
  actionLabel: string
  /** Workspace chooser inside this site. */
  demoPath?: string
  /** Externally hosted demonstration (Government). */
  demoUrl?: string
  detail: string
  workflow: string[]
  note?: string
  /** Demo notice shown for working demos. */
  demoNotice?: string
  /** Existing site page for this domain, when one exists. */
  relatedRoute?: { href: string; label: string }
}

export const demoCenterPath = '/noetivra/demo-center'

/** Published Noetivra workspace project hosting all domain demos. */
export const demoHubUrl = 'https://project-citizen-workspace-hub-602.magicpatterns.app'

export const governmentDemoUrl = `${demoHubUrl}/demos/government`

export const governmentDemoNotice = 'Fictional records · Browser-local saving · Demonstration workspace access'

/** Shared explanation of how the seven domains relate. */
export const sharedFoundationCopy =
  'All seven domains share the same infrastructure—SKP for governed knowledge, HCF methods where capability development applies, and the Noetivra workspace—while each keeps its own workflows, records and responsibilities.'

/** Keeps demonstrations distinct from production milestones (shared availability paragraph). */
export const demoMilestoneNote = demoAvailability

/** Short, distinct summaries of what each demo shows. */
export const domainSummaries: Record<DemoDomain['id'], string> = {
  government: 'Follow development and support requests alongside area reporting and institutional review.',
  research: 'Trace relationships to source passages, review proposals and explore approved knowledge.',
  education: 'Complete a task, receive feedback, practise and compare reassessment evidence.',
  talent: 'Review work samples against role criteria and record human decisions separately from development findings.',
  leadership: 'Explain consequential decisions, review trade-offs with a coach and revisit them after practice.',
  community: 'Connect personal goals, mentoring and practical support through agreed, purpose-limited referrals.',
  clinical:
    'Distinguish patient reports, recorded measurements, professional observations and interpretations within documentation review.',
}

/** Full external demo URL for a domain's workspace chooser. */
export const demoUrlFor = (id: DemoDomain['id']) => `${demoHubUrl}/demos/${id}`

export const demoStatusLabels: Record<DemoStatus, { label: string; meaning: string }> = {
  interactive: { label: 'Interactive demo', meaning: 'Complete sample workflows with working records you can change.' },
  scoped: {
    label: 'Scoped interactive demo',
    meaning: 'A working demonstration of a deliberately narrow part of the solution.',
  },
  planned: {
    label: 'Planned demo',
    meaning: 'The intended experience is described, but an interactive demonstration is not yet available.',
  },
}

export const demoDomains: DemoDomain[] = [
  {
    id: 'government',
    title: 'Government & Public Institutions',
    solutionLabel: 'National Capability and Development System',
    description:
      'Connect citizen development and practical support with program delivery, geographic oversight and accountable institutional decisions.',
    workspaces: ['Citizen Workspace', 'Administration Control Panel'],
    status: 'interactive',
    actionLabel: 'Try the Government workspace',
    demoPath: '/demos/government',
    demoUrl: governmentDemoUrl,
    detail:
      'Explore the fictional Republic of Nordland. Follow a citizen’s development and support journey, review referrals and appeals, and drill from national reporting through regions, states, provinces and cities to districts.',
    workflow: [],
    demoNotice: governmentDemoNotice,
    relatedRoute: { href: '/government', label: 'Government solution' },
  },
  {
    id: 'research',
    title: 'Scientific Research',
    solutionLabel: 'Noetivra Research',
    description:
      'Connect concepts, studies, methods and findings while keeping supporting passages, interpretation limits and review decisions visible.',
    workspaces: ['Research Workspace', 'Curation & Administration'],
    status: 'interactive',
    actionLabel: 'Try the Research workspace',
    demoPath: '/demos/research',
    demoUrl: `${demoHubUrl}/demos/research`,
    detail:
      'Follow a research question from source passages to reviewed relationships. Propose a relationship, check it against its evidence, approve it and see it appear in the knowledge map.',
    workflow: ['Read sources', 'Propose relationships', 'Review evidence', 'Approve knowledge', 'Compare findings'],
    relatedRoute: { href: '/solutions#scientific-research', label: 'Scientific research solution' },
  },
  {
    id: 'education',
    title: 'Education & Learning',
    solutionLabel: 'Noetivra for Education',
    description: 'Build reasoning and self-direction through evidence, feedback and practice.',
    workspaces: ['Learner Workspace', 'Educator Workspace'],
    status: 'interactive',
    actionLabel: 'Try the Education workspace',
    demoPath: '/demos/education',
    demoUrl: `${demoHubUrl}/demos/education`,
    detail:
      'A fictional adult learner interprets mixed course evidence, receives criterion-based feedback from an educator, practises and returns to a changed scenario.',
    workflow: ['Assess', 'Review', 'Develop', 'Reassess'],
    relatedRoute: { href: '/education', label: 'Education solution' },
  },
  {
    id: 'talent',
    title: 'Employers & Workforce',
    solutionLabel: 'Noetivra Talent',
    description: 'Connect role requirements to work samples, transparent review and development.',
    workspaces: ['Candidate / Employee Workspace', 'Employer Reviewer Workspace'],
    status: 'interactive',
    actionLabel: 'Try the Talent workspace',
    demoPath: '/demos/talent',
    demoUrl: `${demoHubUrl}/demos/talent`,
    detail:
      'Compare two fictional work samples against the same role criteria, document reviewer reasoning and record human decisions separately from development feedback.',
    workflow: ['Define requirements', 'Collect evidence', 'Review findings', 'Document a human decision', 'Support development'],
    note: 'Development guidance and consequential hiring decisions remain distinct. Assessment methods require evidence appropriate to their intended use.',
    relatedRoute: { href: '/employers', label: 'Employers & Workforce solution' },
  },
  {
    id: 'leadership',
    title: 'Leadership Development',
    solutionLabel: 'Noetivra Leadership',
    description: 'Make consequential decisions inspectable, then connect feedback to coached practice.',
    workspaces: ['Participant Workspace', 'Coach Workspace'],
    status: 'interactive',
    actionLabel: 'Try the Leadership workspace',
    demoPath: '/demos/leadership',
    demoUrl: `${demoHubUrl}/demos/leadership`,
    detail:
      'A participant works through a service-outage decision, receives coaching feedback, rehearses a decision briefing and revisits the decision when the situation changes.',
    workflow: ['Assess', 'Review', 'Develop', 'Reassess'],
    note: 'Leadership exercises can also be configured within employer, government, education and community programs.',
    relatedRoute: { href: '/leadership', label: 'Leadership solution' },
  },
  {
    id: 'community',
    title: 'Civil Society & Community',
    solutionLabel: 'Noetivra for Communities',
    description: 'Connect personal goals, mentorship and practical support through respectful participation.',
    workspaces: ['Participant Workspace', 'Program Team Workspace'],
    status: 'interactive',
    actionLabel: 'Try the Community workspace',
    demoPath: '/demos/community',
    demoUrl: `${demoHubUrl}/demos/community`,
    detail:
      'Plan a practical next step with a participant, then record a purpose-limited partner referral that shares only what the participant agreed to.',
    workflow: ['Identify a goal', 'Connect support', 'Deliver activities', 'Review progress', 'Agree next steps'],
    note: 'Community organizations can participate in shared programs without receiving unrestricted access to government or partner records.',
    relatedRoute: { href: '/civil-society', label: 'Community & Civil Society solution' },
  },
  {
    id: 'clinical',
    title: 'Clinical Review',
    solutionLabel: 'Noetivra Clinical',
    description: 'A clearer clinical picture through structured documentation and professional review.',
    workspaces: ['Clinician Workspace', 'Authorized Review Workspace'],
    status: 'scoped',
    actionLabel: 'Try the Clinical workspace',
    demoPath: '/demos/clinical',
    demoUrl: `${demoHubUrl}/demos/clinical`,
    detail:
      'One fictional adult patient across two visits. Keep patient reports, measurements, observations and interpretations distinct, review documentation and append addenda after review.',
    workflow: ['Gather history', 'Categorize entries', 'Review documentation', 'Append addenda'],
    note: 'Clinical review does not require civic judgment assessment. Clinicians retain responsibility for assessment and diagnosis.',
    relatedRoute: { href: '/solutions#clinical', label: 'Clinical solution' },
  },
]

/** Groups the domains by the kind of work they demonstrate, for easier scanning. */
export const demoDomainGroups: { title: string; description: string; ids: DemoDomain['id'][] }[] = [
  {
    title: 'Knowledge, services and records',
    description: 'Evidence, public services and professional documentation, each with its own records and review rules.',
    ids: ['government', 'research', 'clinical'],
  },
  {
    title: 'Capability development',
    description: 'Four settings that share one cycle: Assess → Review → Develop → Reassess.',
    ids: ['education', 'talent', 'leadership', 'community'],
  },
]

export const demoDomainHref = (id: DemoDomain['id']) => `${demoCenterPath}#${id}`

export const getDemoDomain = (id: DemoDomain['id']) => demoDomains.find((domain) => domain.id === id)

export const governmentWorkspacePreviews = [
  { title: 'Citizen Workspace', body: 'Personal development, services, requests and review.' },
  { title: 'Administration Control Panel', body: 'Citizen records, delivery workflows and geographic oversight.' },
]

export const foundationCards = [
  {
    name: 'SKP — Connected knowledge',
    body: 'Explore sources, concepts, relationships and approved versions with their provenance.',
    href: '/semantic-knowledge',
  },
  {
    name: 'HCF — Assessment and development',
    body: 'Connect structured tasks, review, development activities and reassessment where capability development is part of the solution.',
    href: '/human-capability',
  },
  {
    name: 'Noetivra — Interactive workspaces',
    body: 'Bring evidence, workflows and decisions into clear interfaces for participants, professionals and administrators.',
    href: '/noetivra',
  },
]
